import { defaultSoundMap, type SoundEffectId } from './creatorPacks';
import type { TableSoundEvent } from '../engine/tables/definition';

type VariantMap = Partial<Record<TableSoundEvent, readonly SoundEffectId[]>>;

const PACK_VARIANTS: Record<string, VariantMap> = {
  'classic-mechanical': {
    flipper: ['coil-clack', 'coil-heavy', 'coil-clack'],
    bumper: ['pop-chime', 'pop-deep', 'pop-chime'],
    slingshot: ['rubber-snap', 'target-knock', 'rubber-snap'],
    target: ['target-knock', 'coil-clack', 'target-knock'],
    rollover: ['lane-ping', 'pop-chime', 'lane-ping'],
    ramp: ['ramp-bell', 'lane-ping', 'ramp-bell'],
    plunger: ['plunger-spring', 'coil-heavy'],
    nudge: ['nudge-thump', 'coil-heavy', 'nudge-thump'],
    drain: ['drain-drop', 'coil-heavy', 'drain-drop'],
    jackpot: ['jackpot-fanfare', 'pop-chime', 'ramp-bell'],
  },
  'solid-state-80s': {
    flipper: ['coil-heavy', 'coil-clack'],
    bumper: ['pop-deep', 'target-zap', 'pop-deep'],
    slingshot: ['rubber-snap', 'target-zap'],
    target: ['target-zap', 'lane-bleep', 'target-zap'],
    rollover: ['lane-bleep', 'lane-ping', 'lane-bleep'],
    ramp: ['ramp-rise', 'lane-bleep', 'ramp-rise'],
    plunger: ['plunger-spring', 'coil-heavy'],
    drain: ['drain-drop', 'lane-bleep'],
    jackpot: ['jackpot-fanfare', 'ramp-rise', 'lane-bleep'],
  },
  'neon-future': {
    flipper: ['coil-heavy', 'target-zap', 'coil-heavy'],
    bumper: ['pop-deep', 'target-zap', 'lane-bleep'],
    slingshot: ['target-zap', 'rubber-snap', 'target-zap'],
    target: ['target-zap', 'lane-bleep', 'ramp-rise'],
    rollover: ['lane-bleep', 'target-zap', 'lane-bleep'],
    ramp: ['ramp-rise', 'target-zap', 'save-rise'],
    plunger: ['plunger-spring', 'ramp-rise'],
    drain: ['drain-drop', 'target-zap'],
    jackpot: ['jackpot-fanfare', 'ramp-rise', 'save-rise'],
  },
  'haunted-arcade': {
    flipper: ['coil-heavy', 'coil-clack', 'coil-heavy'],
    bumper: ['pop-deep', 'coil-heavy', 'pop-deep'],
    slingshot: ['rubber-snap', 'target-knock'],
    target: ['target-knock', 'coil-heavy', 'target-knock'],
    rollover: ['lane-ping', 'target-knock'],
    ramp: ['ramp-bell', 'drain-drop', 'ramp-bell'],
    plunger: ['plunger-spring', 'coil-heavy'],
    nudge: ['nudge-thump', 'coil-heavy'],
    drain: ['drain-drop', 'coil-heavy', 'drain-drop'],
    'prop-appear': ['save-rise', 'ramp-bell'],
    'prop-destroy': ['jackpot-fanfare', 'drain-drop'],
  },
  'royal-fantasy': {
    flipper: ['coil-clack', 'lane-ping'],
    bumper: ['pop-chime', 'lane-ping', 'pop-chime'],
    slingshot: ['rubber-snap', 'lane-ping'],
    target: ['lane-ping', 'pop-chime', 'target-knock'],
    rollover: ['lane-ping', 'pop-chime', 'ramp-bell'],
    ramp: ['ramp-bell', 'save-rise', 'ramp-bell'],
    drain: ['drain-drop', 'ramp-bell'],
    'ball-save': ['save-rise', 'ramp-bell'],
    jackpot: ['jackpot-fanfare', 'ramp-bell', 'save-rise'],
  },
  'casino-night': {
    flipper: ['coil-clack', 'coil-heavy'],
    bumper: ['pop-chime', 'lane-ping', 'pop-chime'],
    slingshot: ['rubber-snap', 'lane-ping'],
    target: ['lane-ping', 'target-knock', 'pop-chime'],
    rollover: ['lane-ping', 'pop-chime', 'ramp-bell'],
    ramp: ['ramp-bell', 'lane-ping', 'save-rise'],
    drain: ['drain-drop', 'lane-ping'],
    'ball-save': ['save-rise', 'ramp-bell'],
    jackpot: ['jackpot-fanfare', 'pop-chime', 'ramp-bell', 'save-rise'],
  },
  'cartoon-chaos': {
    flipper: ['boing', 'chicken-squawk', 'boing'],
    bumper: ['boing', 'fart-pop', 'dog-bark'],
    slingshot: ['chicken-squawk', 'boing', 'cat-meow'],
    target: ['cat-meow', 'dog-bark', 'boing'],
    rollover: ['lane-ping', 'cat-meow', 'slide-whistle'],
    ramp: ['slide-whistle', 'boing', 'cat-purr'],
    plunger: ['boing', 'slide-whistle'],
    nudge: ['dog-bark', 'cat-hiss'],
    drain: ['fart-pop', 'cat-hiss'],
    jackpot: ['chicken-squawk', 'slide-whistle', 'cat-purr'],
  },
  'deep-space': {
    flipper: ['coil-heavy', 'target-zap', 'coil-heavy'],
    bumper: ['pop-deep', 'target-zap', 'pop-deep'],
    slingshot: ['target-zap', 'rubber-snap'],
    target: ['target-zap', 'ramp-rise', 'lane-bleep'],
    rollover: ['lane-bleep', 'target-zap'],
    ramp: ['ramp-rise', 'save-rise', 'target-zap'],
    plunger: ['plunger-spring', 'ramp-rise'],
    nudge: ['nudge-thump', 'coil-heavy'],
    drain: ['drain-drop', 'target-zap', 'drain-drop'],
    jackpot: ['jackpot-fanfare', 'ramp-rise', 'save-rise'],
  },
  silly: {
    flipper: ['boing', 'dog-bark', 'chicken-squawk'],
    bumper: ['fart-pop', 'boing', 'dog-bark'],
    slingshot: ['chicken-squawk', 'cat-meow', 'boing'],
    target: ['dog-bark', 'cat-meow', 'chicken-squawk'],
    rollover: ['cat-meow', 'slide-whistle', 'lane-ping'],
    ramp: ['slide-whistle', 'cat-purr', 'boing'],
    plunger: ['boing', 'chicken-squawk'],
    nudge: ['cat-hiss', 'dog-bark'],
    drain: ['fart-pop', 'cat-hiss', 'fart-pop'],
    'ball-save': ['cat-purr', 'slide-whistle'],
    tilt: ['cat-hiss', 'dog-bark'],
    jackpot: ['slide-whistle', 'chicken-squawk', 'cat-purr'],
  },
};

const counters = new Map<string, number>();

export function soundVariantPool(
  packId: string | undefined,
  event: TableSoundEvent,
  primary: SoundEffectId,
): readonly SoundEffectId[] {
  const resolvedPackId = packId ?? 'classic-mechanical';
  const defaultEffect = defaultSoundMap(resolvedPackId)[event];

  // Creator-authored event/part overrides should stay exact. Variation is only
  // applied to the built-in pack defaults.
  if (primary !== defaultEffect) return [primary];

  const variants = PACK_VARIANTS[resolvedPackId]?.[event];
  if (!variants?.length) return [primary];
  return variants.includes(primary) ? variants : [primary, ...variants];
}

export function soundEffectsForPack(packId: string | undefined): SoundEffectId[] {
  const resolvedPackId = packId ?? 'classic-mechanical';
  const effects = new Set<SoundEffectId>(Object.values(defaultSoundMap(resolvedPackId)));
  for (const variants of Object.values(PACK_VARIANTS[resolvedPackId] ?? {})) {
    for (const effect of variants ?? []) effects.add(effect);
  }
  return [...effects];
}

export function nextSoundVariant(
  packId: string | undefined,
  event: TableSoundEvent,
  primary: SoundEffectId,
): SoundEffectId {
  const variants = soundVariantPool(packId, event, primary);
  if (variants.length === 1) return variants[0]!;

  const key = `${packId ?? 'classic-mechanical'}:${event}:${primary}`;
  const nextIndex = counters.get(key) ?? 0;
  counters.set(key, nextIndex + 1);
  return variants[nextIndex % variants.length]!;
}
